import axios from "axios";
import env from "../variables/config";

axios.defaults.baseURL = env.apiUrl;
const config = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

export interface AccessCode {
    _id: string;
    code: string;
    label?: string;
    premium: boolean;
    trialDays: number;
    maxUses: number; // 0 = unlimited
    usageCount: number;
    active: boolean;
    expiresAt?: string;
    lastUsedAt?: string;
    createdBy?: string;
    createdAt: string;
    accounts: number;
    premiumAccounts: number;
}

export interface AccessCodeUser {
    _id: string;
    email: string;
    name?: string;
    company?: string;
    isSub: boolean;
    trialEndsAt?: string;
    createdAt?: string;
    lastConnection?: string;
}

export type AccessCodeFields = Partial<Pick<AccessCode, "code" | "label" | "premium" | "trialDays" | "maxUses" | "active">> & { expiresAt?: string | null };

const errorMessage = (e: any) => e?.response?.data?.error || "Une erreur est survenue";

export const getAccessCodes = async () => {
    const res = await axios.get("/admin/access-codes", config());
    return res.data.codes as AccessCode[];
};

export const createAccessCode = async (fields: AccessCodeFields): Promise<{ code?: AccessCode; error?: string }> => {
    try {
        const res = await axios.post("/admin/access-codes", fields, config());
        return res.data;
    } catch (e) {
        return { error: errorMessage(e) };
    }
};

export const updateAccessCode = async (id: string, fields: AccessCodeFields): Promise<{ code?: AccessCode; error?: string }> => {
    try {
        const res = await axios.patch(`/admin/access-codes/${id}`, fields, config());
        return res.data;
    } catch (e) {
        return { error: errorMessage(e) };
    }
};

export const deleteAccessCode = async (id: string): Promise<{ error?: string }> => {
    try {
        await axios.delete(`/admin/access-codes/${id}`, config());
        return {};
    } catch (e) {
        return { error: errorMessage(e) };
    }
};

export const getAccessCodeUsers = async (id: string) => {
    const res = await axios.get(`/admin/access-codes/${id}/users`, config());
    return res.data.users as AccessCodeUser[];
};

/**
 * Creates a client's account with this code (or, with `applyToExisting`, applies the code to the
 * account that already has this email). `activationToken` comes back when no password was given:
 * the client sets theirs at /auth/reset-password/:token. `exists` = an account already has this email.
 */
export const createAccountWithCode = async (
    id: string,
    body: { email: string; fullName?: string; company?: string; password?: string; applyToExisting?: boolean }
): Promise<{ user?: AccessCodeUser; created?: boolean; activationToken?: string; exists?: AccessCodeUser; error?: string }> => {
    try {
        const res = await axios.post(`/admin/access-codes/${id}/accounts`, body, config());
        return res.data;
    } catch (e: any) {
        if (e?.response?.status === 409 && e.response.data?.user) return { exists: e.response.data.user };
        return { error: errorMessage(e) };
    }
};

export const signupLink = (code: string) => `${window.location.origin}/auth/register?trial_acces=${encodeURIComponent(code)}`;
export const activationLink = (token: string) => `${window.location.origin}/auth/reset-password/${token}`;
